import { DownloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Descriptions, Row, Skeleton, Space, Statistic, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { useLedger } from '../../api/hooks';
import type { LedgerEntry } from '../../types';
import { apiDate, date, money } from '../../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  ProfitPayment: 'green',
};

export default function LedgerTab({ investorId }: { investorId: number }) {
  const { data, isLoading } = useLedger(investorId);
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('year'), dayjs()]);

  // The statement is the one PDF offered here: it covers every investment, withdrawal and profit
  // payment in the period, so the per-transaction receipts would only repeat it a row at a time.
  const downloadStatement = () =>
    downloadFile(
      `/reports/investor-statement/${investorId}?from=${apiDate(range[0])}&to=${apiDate(range[1])}&format=pdf`,
      `ledger_statement_${apiDate(range[0])}_to_${apiDate(range[1])}.pdf`,
    ).catch((err) => message.error(errorMessage(err)));

  if (isLoading || !data) return <Skeleton active />;

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>Statement period:</span>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(values) => values && setRange(values as [dayjs.Dayjs, dayjs.Dayjs])}
          />
          <Button type="primary" icon={<DownloadOutlined />} onClick={downloadStatement}>
            Download ledger statement (PDF)
          </Button>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Current principal" value={money(data.currentPrincipal)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Outstanding profit" value={money(data.outstandingProfit)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Total liability"
              value={money(data.totalLiability)}
              prefix="PKR"
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} style={{ marginTop: 16 }}>
        <Descriptions.Item label="First investment">{date(data.firstInvestmentDate)}</Descriptions.Item>
        <Descriptions.Item label="As of">{date(data.asOf)}</Descriptions.Item>
        <Descriptions.Item label="Initial deposit">{money(data.initialDeposit)}</Descriptions.Item>
        <Descriptions.Item label="Additional deposits">{money(data.additionalDeposits)}</Descriptions.Item>
        <Descriptions.Item label="Total deposited">{money(data.totalDeposited)}</Descriptions.Item>
        <Descriptions.Item label="Total withdrawn">{money(data.totalWithdrawn)}</Descriptions.Item>
        <Descriptions.Item label="Total accrued profit">{money(data.totalAccruedProfit)}</Descriptions.Item>
        <Descriptions.Item label="Total paid profit">{money(data.totalPaidProfit)}</Descriptions.Item>
      </Descriptions>

      <Card title="Transactions" size="small" style={{ marginTop: 16 }}>
        <Table<LedgerEntry>
          rowKey={(row, index) => `${row.date}-${row.type}-${index}`}
          size="small"
          dataSource={data.entries}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Debit', dataIndex: 'debit', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Credit', dataIndex: 'credit', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Principal', dataIndex: 'runningPrincipal', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
